// Dependencies
import Hls from 'hls.js';
import moment from 'moment-timezone';
import 'moment/min/locales';
import { isAndroid, isIOS } from 'react-device-detect';
import { type Camera, type DownloadOption } from '../types/camera.types';
import { type Event, type Info, type Share, type VideoLinkItem } from '../types/event.types';

// Utils
import { type LocaleDefinition } from '@solinkcorp/locale';
import { type Device } from '../types/device.types';
import CloudAPI from './api';

/* Determines whether a mac or windows user */
export const windowsOrMac = () => {
  const { platform } = window.navigator;

  return platform.startsWith('Mac') ? 'mac' : 'windows';
};

/* Get the download link */
export const getDownloadLink = (camera: Camera) => {
  if (camera && camera.streams) {
    // Playlist object containing links to download and bundle assets
    const playlist = camera.streams.find((s) => s.playlist);
    if (playlist) {
      return {
        url: playlist.download,
        bundle: playlist.bundle,
      };
    }
    return {
      url: null,
      bundle: null,
    };
  }
};

/* Removes the jwt from the url */
export const filterOutJWT = (url: string, jwt: string) => url.replace(`&jwt=${jwt}`, '');

/* Returns the jwt from a url */
export const parseJWT = (url: string) => {
  const urlParams = new URLSearchParams(url);
  return urlParams.get('jwt');
};

/* Get the location name with a given string */
export const getLocationName = (str: string) => {
  const subtitleSplits = str.split(' @ ');
  const locationName = subtitleSplits[1] || subtitleSplits[0];
  return locationName;
};

/* Returns the map link based on if the user is IOS or mac */
export const getMapLink = (address: string) =>
  `http://maps.${isIOS ? 'apple' : 'google'}.com/?q=${address}`;

/* Return the date 30 days after the start of the event */
export const getEventExpirationDate = ({
  eventStartTime,
  timezoneId,
  abbreviate = false,
}: {
  eventStartTime: string;
  timezoneId: string;
  abbreviate?: boolean;
}) => {
  const dateFormat = abbreviate ? 'ddd, MMM DD, YYYY h:mm A' : 'dddd, MMMM DD, YYYY h:mm A';
  return moment(eventStartTime).tz(timezoneId).add(30, 'd').format(dateFormat).toUpperCase();
};

/* Check to see if the Token has expired or not */
export const checkTokenExpiration = (tokenExpiration: number) => {
  const now = new Date();
  const currentTime = Math.round(now.getTime() / 1000);
  return currentTime > tokenExpiration;
};

/* Construct a url for share events page */
export const constructEventURL = (event: Event, info: Info) => {
  const { startTime, locationId, id, type } = event;
  const { tenantId } = info;
  /* Set subtype to the event subtype or an empty string */
  const subtype = event.subtype ? event.subtype : '';
  /* Destrucutre query param values */
  const { showTransactions, showMotion, showBookmarks, showAlarms } = generateParams(type, subtype);

  let urlProtocol = 'https';

  if (isAndroid) {
    urlProtocol = 'solink';
  }

  return `${urlProtocol}://solinkcloud.com/#/events?appDate=${formatTimeEpochSeconds(
    startTime,
  )}&locationId=${locationId}&showTransactions=${showTransactions}&showMotion=${showMotion}&showBookmarks=${showBookmarks}&showAlarms=${showAlarms}&filterFavourites=NO&eventId=${id}&tenantId=${tenantId}&source=share`;
};

/* Construct a url for share events page */
export const constructAlarmEventURL = (url: string) => {
  return isAndroid ? url.replace('https', 'solink') : url;
};

/* Format the video expiration date */
export const formatVideoExpirationDate = ({
  videoExpirationDate,
  timezoneId,
  abbreviate,
}: {
  videoExpirationDate: number;
  timezoneId: string;
  abbreviate?: boolean;
}) => {
  const dateFormat = abbreviate ? 'ddd, MMM DD, YYYY h:mm A' : 'dddd, MMMM DD, YYYY h:mm A';
  const expirationDate = new Date(videoExpirationDate * 1000);
  return moment.tz(expirationDate, timezoneId).format(dateFormat).toUpperCase();
};

export const getUserRestrictions = (shares: Share[], currentUser: Info) => {
  const [currentShare] = shares.filter((share) => share.email === currentUser.sharedTo);
  return currentShare.restrictions;
};
/* Take a date and format it into Epoch Seconds */
export const formatTimeEpochSeconds = (date: string) => Math.round(new Date(date).getTime() / 1000);

export const generateParams = (type: 'bookmark' | 'motion' | 'transaction', subtype: string) => {
  /* Initialize all params to NO */
  let showTransactions = 'NO';
  let showMotion = 'NO';
  let showBookmarks = 'NO';
  let showAlarms = 'NO';

  /* Check the event type and set the appropriate param*/
  if (subtype === 'alarm' && type === 'bookmark') {
    showAlarms = 'YES';
  } else if (type === 'bookmark') {
    showBookmarks = 'YES';
  } else if (type === 'transaction') {
    showTransactions = 'YES';
  } else if (type === 'motion') {
    showMotion = 'YES';
  } else {
    console.error('Invalid event type');
  }
  /* Return an object with the different query param values */
  return { showTransactions, showMotion, showBookmarks, showAlarms };
};

export const shouldUse360Controls = (cameraName: string) => {
  return /360/.test(cameraName) && !isIOS && !isAndroid;
};

export const formatTimeOfDay = ({
  time,
  timezoneId,
}: {
  time: string | number | undefined;
  timezoneId: string | undefined;
}) => {
  if (!time || !timezoneId) return;
  return moment.tz(time, timezoneId).format('h:mm:ss A').toUpperCase();
};

export const formatFullDate = (time?: number, device?: Device | null) => {
  if (!time || !device) return;
  return `${moment.tz(time, device.timezoneId).format('dddd, MMMM DD, YYYY h:mm:ss A')} ${
    device.timezoneName
  }`;
};

export const formatDay = ({
  time,
  timezoneId,
  abbreviate = false,
}: {
  time: string | number | undefined;
  timezoneId: string | undefined;
  abbreviate?: boolean;
}) => {
  if (!time || !timezoneId) return;
  const dateFormat = abbreviate ? 'ddd, MMM DD, YYYY' : 'dddd, MMMM DD, YYYY';
  return moment.tz(time, timezoneId).format(dateFormat).toUpperCase();
};

export const usingProperDeviceVersion = (version: string) => {
  const regex = /^\d+/;
  const found = parseInt(version.match(regex)?.[0] || '0', 10);
  return found >= 37;
};

export const isInterlink = (version: string) => {
  const regex = /^\d+/;
  const found = parseInt(version.match(regex)?.[0] || '0', 10);
  return found >= 7;
};

export const isHlsSupported = () => {
  return Hls.isSupported();
};

export const filterVideoLinkItems = (videoLinkItems: VideoLinkItem[]) => {
  let logItems = videoLinkItems;
  if (logItems.length > 0) {
    let alarmOpenedStartTime: number;

    // Sort the VideoLinkItems
    logItems = videoLinkItems.sort((a, b) =>
      a.timestamp > b.timestamp ? 1 : b.timestamp > a.timestamp ? -1 : 0,
    );

    // Find when the alarm Opend
    logItems.forEach((item) => {
      if (item.title.includes('Alarm Opened')) {
        alarmOpenedStartTime = item.timestamp;
      }
    });

    // Remove Video Link Items before the alarm opened
    logItems = videoLinkItems.filter((item) => item.timestamp >= alarmOpenedStartTime);
  }
  return logItems;
};

export const updateCameras = (cameras: Camera[], videoLinkItems: VideoLinkItem[]) => {
  return cameras.map((camera) => {
    let newUrl;
    const { id } = camera;
    for (const item of videoLinkItems) {
      const {
        cameraId,
        metadata: { videoUrl },
      } = item;
      if (id === cameraId) {
        newUrl = videoUrl;
        break;
      }
    }

    if (newUrl) {
      camera.videoUrl = newUrl;
    }
    return camera;
  });
};

export const getDefaultAlarmCamera = ({
  alarmEvent,
  cameraId,
}: {
  alarmEvent: Event;
  cameraId: string | undefined;
}) => {
  let targetCamera;
  let targetCameraIndex;
  if (cameraId) {
    targetCamera = alarmEvent.cameras.find((camera, index) => {
      targetCameraIndex = index;
      return camera.id === cameraId;
    });
  }
  return {
    defaultCamera: targetCamera ?? alarmEvent.cameras[0],
    defaultCameraIndex: targetCameraIndex ?? 0,
  };
};

export const getVideoLinkItemByTimestampAndCameraId = ({
  alarmEvent,
  cameraId,
  timestamp,
}: {
  alarmEvent: Event;
  timestamp: string | null;
  cameraId: string | null;
}) => {
  return alarmEvent.videoLinkItems?.find(
    (videoLinkItem) =>
      videoLinkItem.timestamp.toString() === timestamp && videoLinkItem.cameraId === cameraId,
  );
};

export const getVideoLinkItemUrl = (camera: Camera, videoLinkItems: VideoLinkItem[]) => {
  let newUrl;
  const { id } = camera;
  for (const item of videoLinkItems) {
    const {
      cameraId,
      metadata: { videoUrl },
    } = item;
    if (id === cameraId) {
      newUrl = videoUrl;
      break;
    }
  }
  return newUrl ? newUrl : camera.videoUrl;
};

export const getDownloadOptionsByCameraId = async (cameras: Camera[], locale: LocaleDefinition) => {
  const promises = cameras.map((camera) => getDownloadOptions(camera, locale));
  const results = await Promise.all(promises);
  const downloadOptions = results.reduce(
    (acc: { [key: string]: DownloadOption }, options, index) => {
      if (Object.keys(options)) {
        acc[cameras[index].chId] = options;
      }
      return acc;
    },
    {},
  );
  return downloadOptions;
};

export const getDownloadOptions = async (camera: Camera, locale: LocaleDefinition) => {
  const is360 = shouldUse360Controls(camera.name);
  const platform = windowsOrMac();
  const download = getDownloadLink(camera);
  const mp4URL = download?.url;
  const bundle = download?.bundle;
  const bundleURL = bundle ? bundle[platform] : null;
  const downloadOptions: DownloadOption = {};

  if (mp4URL) {
    downloadOptions['mp4'] = {
      id: 'raw-video-file',
      text: locale.spa.rawVideoFile,
      type: 'raw',
      url: mp4URL,
    };
  }

  if (is360 && bundleURL && (await CloudAPI.headCheck(bundleURL))) {
    downloadOptions['zip'] = {
      id: 'video-and-player',
      text: locale.spa.windowsSolinkPlayer,
      type: 'bundle',
      url: bundleURL,
    };
  }

  return downloadOptions;
};

export const getStartAndEndTime = (event: Event, sharePage?: boolean) => {
  return {
    startTime: sharePage ? event.shareStartTime : event.startTime,
    endTime: sharePage ? event.shareEndTime : event.endTime,
  };
};

export const hasLiveExpired = (startTime?: any) => {
  if (!startTime) return false;
  const twoHoursFromStartTime = addTwoHours(startTime);
  return Date.now() > twoHoursFromStartTime;
};

export const addTwoHours = (originalTime: string) => {
  return moment(originalTime).add(2, 'hours').valueOf();
};

export const getTimezoneName = (timezoneId: string, date: string | undefined) => {
  return moment.tz(date || Date.now(), timezoneId).format('z');
};

export function addQueryParamToURL(url: string, key: string, value: string) {
  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}${key}=${value}`;
}

export const getPlayListUrl = (camera: Camera) => {
  let url;
  if (camera && camera.streams) {
    camera.streams.forEach((stream) => {
      if (stream.playlist) {
        url = stream.playlist;
      }
    });
  }
  return url;
};
